import DesignTokens from '@tableau/design-tokens';
import { Colors } from '@tableau/icons/util/Colors';
import { TabStyles } from '@tableau/widgets';
import { defaultMemoize } from 'reselect';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { applyColor } from '~src/style/ColorService';

import { classes, cssClass } from '../../../style';
import { LeftNavVisibility } from './LeftNav';

const cssPrefix = 'Navigation';

export namespace NavStyles {
  export const OpenPanelWidth = 240;  // must be kept in sync with updateSiteLogoForm.styl
  export const MinimizedPanelWidth = 66;

  export const LeftPanel = cssClass(cssPrefix, 'left-panel', {
    overflowY: 'auto',
    transition: 'width .3s ease-in-out',
    height: '100%',
    backgroundColor: applyColor(DesignTokens.color.navigation.tabs.vertical.default.default, CommonStyles.Colors.Grey250),  // must be kept in sync with updateSiteLogoForm.styl
    overflowX: 'hidden',
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    boxSizing: 'border-box',
    borderRight: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles.Colors.L6)}`,
    $nest: {
      '@media screen and (max-width: 768px)': {
        display: 'none'
      }
    }
  });

  export const LeftNavExpanded = cssClass(cssPrefix, 'left-panel-shown', {
    width: OpenPanelWidth
  });

  export const MinimizedLeftNav = cssClass(cssPrefix, 'left-panel-minimized', {
    width: MinimizedPanelWidth
  });

  export const MinimizedScrollLeftNav = cssClass(cssPrefix, 'left-panel-minimized-scroll', {
    width: MinimizedPanelWidth + 10,
  });

  export function getLeftNavClassNames(leftNavVisibility: LeftNavVisibility, visibleVerticalScroll: boolean) {
    return classes(
      LeftPanel,
      leftNavVisibility === 'expanded' ? LeftNavExpanded :
        visibleVerticalScroll ? MinimizedScrollLeftNav : MinimizedLeftNav
    );
  }

  export const NavList = cssClass(cssPrefix, 'nav-list', {
    zIndex: 0,
    outline: 'none',
    listStyleType: 'none',
    padding: 0,
    margin: 0,
    listStyle: 'none'
  });

  export const LeftPanelButton = cssClass(cssPrefix, 'left-panel', {
    width: '100%',
  });

  export const ButtonStyle: React.CSSProperties = {
    height: 52,
    paddingRight: 0,
    paddingLeft: 0,
    paddingTop: 0,
    paddingBottom: 0,
    width: '100%',
    marginBottom: 8,
    minWidth: 0,
    border: 'none'
  };

  export const ButtonContentIcon = cssClass(cssPrefix, 'nav-icon', {
    height: 24 + 'px',
    width: 24
  });

  const ButtonContentStyle: React.CSSProperties = {
    fontWeight: 'normal',
    fontSize: '12px',
    color: applyColor(DesignTokens.color.font.default.default, CommonStyles.Colors.NormalTextColor),
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    boxSizing: 'border-box',
    backgroundColor: applyColor(DesignTokens.color.navigation.tabs.vertical.default.default, CommonStyles.Colors.Grey250),  // must be kept in sync with updateSiteLogoForm.styl'
  };

  export const ButtonContent = cssClass(cssPrefix, 'left-panel-shown', {
    ...ButtonContentStyle,
    paddingRight: 4,
  });

  export const ButtonContentVerticalScroll = cssClass(cssPrefix, 'left-panel-minimized', {
    ...ButtonContentStyle,
    paddingRight: 14,
  });

  export function getButtonContentClassNames(visibleVerticalScroll: boolean) {
    return classes(
      visibleVerticalScroll ? ButtonContentVerticalScroll : ButtonContent
    );
  }

  export const NavPanel = cssClass(cssPrefix, 'nav-panel', {
    paddingLeft: 0,
    listStyle: 'none'
  });

  export const navLinkIconSize = 20;
  const navLinkHeight = 40;
  const highlightedNavLinkTextColor = 'rgb(25,25,25)';
  const highlightedNavLinkBackgroundColor = 'rgb(241,241,241)';

  // absolute positioning here so that we can leave a fixed left hand margin
  // and place the selected bar atop the margin.
  // This ensure the margin doesn't change when animating and no weird shifts
  export const SelectedBarStyle: React.CSSProperties = {
    width: 4,
    height: navLinkHeight,
    backgroundColor: applyColor(DesignTokens.color.navigation.tabs.vertical.default.selectededge, 'rgb(25,25,25)'),
    position: 'absolute',
    top: 0,
    left: 0,
  };

  export const DisabledBarStyle = defaultMemoize((selected: boolean) =>
    cssClass(cssPrefix, 'disabled-nav-link', {
      $nest: {
        ' .text': {
          opacity: selected ? 1 : 0.5
        },
        ' .icon': {
          opacity: selected ? 1 : 0.5
        },
        '&:hover .text, &:focus .text': {
          color: applyColor(DesignTokens.color.font.default.default, highlightedNavLinkTextColor),
          opacity: 1
        },
        '&:hover .icon, &:focus .icon': {
          opacity: 1
        },
      },
    })
  );

  export const NavLinkValue = defaultMemoize((selected: boolean) =>
    cssClass(cssPrefix, 'nav-link-value', {
      position: 'relative',
      fontSize: '14px',
      verticalAlign: 'middle',
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      height: navLinkHeight,
      outline: 'none',
      textDecoration: 'none',
      boxSizing: 'border-box',
      backgroundColor: selected ? applyColor(DesignTokens.color.navigation.tabs.vertical.default.selected, highlightedNavLinkBackgroundColor) : 'transparent',
      $nest: {
        [`&:focus-visible`]: {
          ...CommonStyles.Focus.OutlineIndicator,
          backgroundColor: applyColor(DesignTokens.color.navigation.tabs.vertical.default.selected, highlightedNavLinkBackgroundColor),
          zIndex: 1, // ensure adjacent buttons don't occlude the focus indicator box-shadow
        },
        '&:hover': {
          backgroundColor: applyColor(DesignTokens.color.navigation.tabs.vertical.default.hover, CommonStyles.Colors.Grey235),
        },
        '&:hover, &:focus': {
          textDecoration: 'none',
        },
        '> .text': {
          color: selected ? applyColor(DesignTokens.color.font.default.default, highlightedNavLinkTextColor) : applyColor(DesignTokens.color.font.default.default,  CommonStyles.Colors.NormalTextColor),
        },
        '&:hover > .text, &:focus > .text': {
          color: applyColor(DesignTokens.color.font.default.default, highlightedNavLinkTextColor),
        },
      }
    })
  );

  export const NavLinkText = cssClass(cssPrefix, 'nav-link-text', {
    fontWeight: 'normal',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    lineHeight: '16px',
    paddingTop: 2, // center-ish text with icon
  });

  const navIcon: React.CSSProperties = {
    display: 'inline-block',
    height: `${navLinkIconSize}px`,
    width: `${navLinkIconSize}px`,
    marginLeft: 23,
    color: applyColor(DesignTokens.color.font.default.default, Colors.Default),
  };

  export const NavIcon = cssClass(cssPrefix, 'nav-icon-only', {
    ...navIcon,
  });

  export const NavIconByText = cssClass(cssPrefix, 'nav-icon-by-text-only', {
    ...navIcon,
    marginRight: 11,
  });

  const separator: React.CSSProperties = {
    height: 1,
    backgroundColor: applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles.Colors.L9Disabled),
    marginBottom: 16,
    marginTop: 16,
  };

  export const SeparatorCollapsed = cssClass(cssPrefix, 'separator-collapsed', {
    ...separator,
    marginRight: 8,
    marginLeft: 8,
  });

  export const SeparatorExpanded = cssClass(cssPrefix, 'separator-expanded', {
    ...separator,
    marginRight: 12,
    marginLeft: 12,
  });

  export const SeparatorSitePicker = cssClass(cssPrefix, 'separator-site-picker', {
    ...separator,
    marginBottom: TabStyles!.Spacing!.DefaultSpace,
    marginTop: TabStyles!.Spacing!.DefaultSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
    marginLeft: TabStyles.Spacing.DefaultSpace,
  });

  export const NavPanelFlyoutContainer = cssClass(cssPrefix, 'nav-panel-flyout-container', {
    width: '100%',
    height: '100%',
    transition: 'transform .3s ease-in-out',
    transform: 'translateX(-100%)',
    willChange: 'transform',
  });

  // This must match the time in transition below
  export const NavPanelFlyoutHideAnimationTime = 150;
  export const NavPanelFlyoutContainerVisible = cssClass(cssPrefix, 'nav-panel-flyout-container-visible', {
    transform: 'translateX(0)',
    transition: 'transform .15s linear',
  });

  export function getNavPanelFlyoutContainer(startingShow: boolean) {
    return classes(
      NavPanelFlyoutContainer,
      startingShow ? undefined : NavPanelFlyoutContainerVisible
    );
  }

  export const LogoMarginTop = 0;
  export const LogoMarginBottom = 24;  // keep in sync with updateSiteLogoForm.styl

  export const LogoImage: React.CSSProperties = {
    display: 'flex',
    marginRight: 'auto',
    marginLeft: 'auto',
  };

  export const LogoFocus = cssClass(cssPrefix, 'logo-focus', {
    borderRadius: '1px',
    $nest: {
      [`&:focus-visible`]: CommonStyles.Focus.OutlineIndicator
    }
  });
}
